<template>
  <div>
    <div>
      <div v-if="pageLoading" class="text-center" style="padding: 80px 0;">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Carregando...</span>
        </div>
        <p class="mt-2">Carregando estoque...</p>
      </div>

      <template v-else>
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-1 stock-header" style="gap: 8px;">
          <h3 class="mb-0">Estoque</h3>
          <div class="stock-header-actions">
            <button type="button" class="btn btn-outline-secondary mr-2" @click="confirmDownloadReport">
              Baixar relatório
            </button>
            <router-link to="/stock_movements/add" class="btn btn-primary">Nova Movimentação</router-link>
          </div>
        </div>

        <div class="card stock-filters">
          <button
            type="button"
            class="btn-filters-toggle d-md-none"
            @click="filtersOpen = !filtersOpen"
          >
            <span>Filtros</span>
            <span class="chevron" :class="{ open: filtersOpen }">▾</span>
          </button>
          <div class="card-body" :class="{ 'card-body-mobile-open': filtersOpen }">
            <div class="row">
              <div class="col-md-6">
                <label>Modelo</label>
                <input type="text" class="form-control" v-model="filters.name" placeholder="Buscar por modelo..." @keyup.enter="applyFilters">
              </div>
              <div class="col-md-6">
                <label>Fabricante</label>
                <v-select
                  v-model="filters.manufacturer_id"
                  :options="manufacturers"
                  :reduce="(m) => m.id"
                  label="name"
                  placeholder="Selecione"
                ></v-select>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-md-12">
                <button class="btn btn-primary" @click="applyFilters">Filtrar</button>
                <button class="btn btn-secondary ml-2" @click="clearFilters">Limpar filtros</button>
              </div>
            </div>
          </div>
        </div>

        <div class="card mt-0 stock-table-card">
          <div class="row" v-if="loading">
            <div class="col-md-12">
              <div class="text-center" style="padding: 50px;">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Carregando produtos...</span>
                </div>
                <p class="mt-2">Carregando produtos...</p>
              </div>
            </div>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-striped table-bordered table-hover stock-table">
              <thead>
                <tr>
                  <th></th>
                  <th class="sortable" @click="sortBy('name')">
                    Modelo
                    <i class="fas" :class="sortIconClass('name')"></i>
                  </th>
                  <th>Fabricante</th>
                  <th class="sortable text-center" @click="sortBy('total_quantity')">
                    Quantidade
                    <i class="fas" :class="sortIconClass('total_quantity')"></i>
                  </th>
                  <th class="sortable text-center" @click="sortBy('average_price')">
                    Preço médio
                    <i class="fas" :class="sortIconClass('average_price')"></i>
                  </th>
                  <th class="sortable text-center" @click="sortBy('total_price')">
                    Preço total
                    <i class="fas" :class="sortIconClass('total_price')"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!models.length">
                  <td colspan="6" class="text-center py-4">Nenhum registro encontrado</td>
                </tr>
                <template v-for="model in models">
                  <tr
                    :key="model.product_model_id"
                    class="model-row"
                    @click="toggleExpand(model)"
                  >
                    <td class="text-center expand-col">
                      <i class="fas" :class="expanded[model.product_model_id] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    </td>
                    <td data-label="Modelo">{{ model.model_name }}</td>
                    <td data-label="Fabricante">{{ model.manufacturer_name || "-" }}</td>
                    <td data-label="Quantidade" class="text-center">{{ model.total_quantity }}</td>
                    <td data-label="Preço médio" class="text-center">{{ formatPrice(model.average_price) }}</td>
                    <td data-label="Preço total" class="text-center">{{ formatPrice(model.total_price) }}</td>
                  </tr>
                  <tr v-if="expanded[model.product_model_id]" :key="'detail-' + model.product_model_id" class="detail-row">
                    <td colspan="6">
                      <div v-if="detailsLoading[model.product_model_id]" class="text-center py-3">
                        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                      </div>
                      <table v-else class="table table-sm detail-table mb-0">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Unidade</th>
                            <th>Patrimônio</th>
                            <th>Fornecedor</th>
                            <th>Categoria</th>
                            <th>Preço</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="!(details[model.product_model_id] || []).length">
                            <td colspan="7" class="text-center py-2">Nenhum produto encontrado</td>
                          </tr>
                          <tr v-for="product in details[model.product_model_id]" :key="product.id">
                            <td>{{ product.id }}</td>
                            <td>{{ product.current_unit ? product.current_unit.name : "Sem Unidade" }}</td>
                            <td>{{ product.barcode || "-" }}</td>
                            <td>{{ product.supplier ? product.supplier.name : "-" }}</td>
                            <td>{{ product.category ? product.category.name : "-" }}</td>
                            <td>{{ formatPrice(product.purchase_value) }}</td>
                            <td class="text-center">
                              <span class="soft-badge" :class="statusBadgeClass(product.status)">
                                {{ statusLabel(product.status) }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div class="pagination-bar">
            <div class="per-page-selector">
              Registros por página:
              <select class="form-control d-inline-block w-auto ml-1" v-model.number="pagination.perPage" @change="changePerPage">
                <option v-for="v in pagination.perPageOptions" :key="v" :value="v">{{ v }}</option>
              </select>
            </div>

            <div class="col-md-6 mt-1 mx-auto d-flex justify-content-center align-items-center">
              <pagination
                :records="totalModels"
                :options="paginationOptions"
                :per-page="pagination.perPage"
                @paginate="onPaginate"
              ></pagination>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { Pagination } from "vue-pagination-2";
import vSelect from "vue-select";
import { openAuthenticatedPdf } from "../../utils/openAuthenticatedPdf";

export default {
  components: {
    Pagination,
    vSelect,
  },
  data: function () {
    return {
      pageLoading: true,
      loading: false,
      filtersOpen: false,

      filters: {
        name: "",
        manufacturer_id: null,
      },

      manufacturers: [],

      models: [],
      totalModels: 0,
      pagination: {
        page: 1,
        perPage: 10,
        perPageOptions: [10, 25, 50, 100],
      },
      paginationOptions: {
        texts: {
          count: "Mostrando {from} de {to} até {count} de registros",
        },
        chunk: 4,
        chunksNavigation: "fixed",
      },
      sort: {
        column: "name",
        ascending: true,
      },

      expanded: {},
      details: {},
      detailsLoading: {},
    };
  },

  async mounted() {
    this.pageLoading = true;

    try {
      await this.getManufacturers();
      await this.fetchModels();
    } catch (e) {
      console.error(e);
    } finally {
      this.pageLoading = false;
    }
  },

  methods: {
    confirmDownloadReport() {
      const self = this;
      swal({
        title: "Baixar relatório?",
        text: "O relatório em PDF será gerado respeitando os filtros aplicados no momento.",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#7367f0",
        confirmButtonText: "Sim, baixar!",
        buttons: {
          catch: { text: "Não", value: "cancel", className: "btn-danger" },
          confirm: { text: "Sim", value: "confirm" },
        },
      }).then((value) => {
        if (value !== "confirm") return;
        self.downloadReport();
      });
    },

    downloadReport() {
      const self = this;
      const f = self.filters;
      const params = new URLSearchParams();

      if (f.name) params.append("name", f.name);
      if (f.manufacturer_id) params.append("manufacturer_id", f.manufacturer_id);

      const url = self.$store.state.api + "products/pdf?" + params.toString();
      openAuthenticatedPdf(url).catch(() => {
        self.$message(null, "Não foi possível gerar o PDF", "error");
      });
    },

    fetchModels() {
      const self = this;
      const f = this.filters;

      let qs = "";
      if (f.name) qs += "name=" + encodeURIComponent(f.name) + "&";
      if (f.manufacturer_id) qs += "manufacturer_id=" + f.manufacturer_id + "&";

      qs += "limit=" + this.pagination.perPage + "&page=" + this.pagination.page + "&";
      qs += (this.sort.ascending ? "orderByAsc=" : "orderByDesc=") + this.sort.column;

      self.loading = true;
      self.expanded = {};
      self.details = {};
      return axios
        .get(self.$store.state.api + "products/grouped-by-model?" + qs)
        .then((response) => {
          self.models = response.data.data;
          self.totalModels = response.data.count;
          self.loading = false;
        })
        .catch((error) => {
          self.$message(null, error.response.data, "error");
          self.loading = false;
        });
    },

    toggleExpand(model) {
      const id = model.product_model_id;
      this.$set(this.expanded, id, !this.expanded[id]);

      if (this.expanded[id] && !this.details[id]) {
        this.fetchModelProducts(id);
      }
    },

    fetchModelProducts(productModelId) {
      const self = this;
      this.$set(this.detailsLoading, productModelId, true);

      let qs = "product_model_id=" + productModelId + "&status=1&";
      qs += "with[]=currentUnit&with[]=supplier&with[]=category&";
      qs += "paginated=false&orderByDesc=id";

      return axios
        .get(self.$store.state.api + "products?" + qs)
        .then((response) => {
          self.$set(self.details, productModelId, response.data.data);
          self.$set(self.detailsLoading, productModelId, false);
        })
        .catch((error) => {
          self.$message(null, error.response.data, "error");
          self.$set(self.detailsLoading, productModelId, false);
        });
    },

    applyFilters() {
      this.pagination.page = 1;
      this.fetchModels();
    },

    clearFilters() {
      this.filters = {
        name: "",
        manufacturer_id: null,
      };
      this.applyFilters();
    },

    onPaginate(page) {
      this.pagination.page = page;
      this.fetchModels();
    },

    changePerPage() {
      this.pagination.page = 1;
      this.fetchModels();
    },

    sortBy(column) {
      if (this.sort.column === column) {
        this.sort.ascending = !this.sort.ascending;
      } else {
        this.sort.column = column;
        this.sort.ascending = true;
      }
      this.fetchModels();
    },

    sortIconClass(column) {
      if (this.sort.column !== column) return "fa-sort";
      return this.sort.ascending ? "fa-chevron-up" : "fa-chevron-down";
    },

    statusLabel(status) {
      const labels = { 1: "Em estoque", 2: "Em uso", 3: "Manutenção", 0: "Baixado" };
      return labels[status] ?? "Baixado";
    },

    statusBadgeClass(status) {
      const classes = {
        1: "badge-success",
        2: "badge-primary",
        3: "badge-warning",
        0: "badge-danger",
      };
      return classes[status] ?? "badge-secondary";
    },

    formatPrice(value) {
      if (!value) return "-";
      return (
        "R$ " +
        Number(value).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },

    getManufacturers() {
      const self = this;
      return axios
        .get(self.$store.state.api + "manufacturers?paginated=false")
        .then((response) => {
          self.manufacturers = response.data.data;
        })
        .catch((error) => {
          self.$message(null, error.response.data, "error");
        });
    },
  },
};
</script>

<style scoped>
.stock-table {
  margin-bottom: 0;
  font-size: 0.95rem;
}

.stock-table thead th.sortable {
  cursor: pointer;
  user-select: none;
}

.stock-table td,
.stock-table th {
  padding: 0.9rem 0.75rem;
}

.stock-table thead th {
  background-color: #283046 !important;
  padding-top: 0.7rem;
  padding-bottom: 0.7rem;
  vertical-align: middle;
  border-left: none !important;
  border-right: none !important;
}

.stock-table tbody tr,
.stock-table tbody tr:nth-of-type(odd),
.stock-table tbody tr:nth-of-type(even) {
  background-color: #132133 !important;
}

.stock-table tbody td {
  background-color: #132133 !important;
}

.stock-table tr.model-row {
  cursor: pointer;
}

.stock-table tr.detail-row td {
  background-color: #0e1926 !important;
  padding: 0.75rem 1.25rem !important;
}

.expand-col {
  width: 32px;
  color: rgba(159, 168, 218, 0.65);
}

.detail-table {
  font-size: 0.85rem;
  margin-bottom: 0;
}

.detail-table th,
.detail-table td {
  background-color: transparent !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.stock-table .soft-badge {
  display: inline-block;
  white-space: normal;
  max-width: 140px;
  padding: 0.5em 0.75em;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
  border-radius: 6px;
  border: 1px solid transparent;
}

.stock-table .soft-badge.badge-danger {
  background-color: rgba(234, 84, 85, 0.12) !important;
  border-color: rgba(234, 84, 85, 0.4);
  color: #d97577 !important;
}

.stock-table .soft-badge.badge-warning {
  background-color: rgba(255, 159, 67, 0.12) !important;
  border-color: rgba(255, 159, 67, 0.4);
  color: #d99a5c !important;
}

.stock-table .soft-badge.badge-success {
  background-color: rgba(40, 199, 111, 0.12) !important;
  border-color: rgba(40, 199, 111, 0.4);
  color: #5cb98a !important;
}

.stock-table .soft-badge.badge-primary {
  background-color: rgba(115, 103, 240, 0.12) !important;
  border-color: rgba(115, 103, 240, 0.4);
  color: #8b82df !important;
}

.stock-table .soft-badge.badge-secondary {
  background-color: rgba(130, 134, 139, 0.12) !important;
  border-color: rgba(130, 134, 139, 0.4);
  color: #a9adb3 !important;
}

.pagination-bar {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 16px;
  position: relative;
}

.pagination-bar .per-page-selector {
  position: absolute;
  left: 16px;
  top: 16px;
  display: flex;
  align-items: center;
  height: 38px;
}

.pagination-bar ::v-deep .VuePagination {
  text-align: center;
  margin-bottom: 0;
}

.pagination-bar ::v-deep .VuePagination ul.pagination {
  margin-bottom: 0;
  justify-content: center;
}

.pagination-bar ::v-deep .VuePagination__count {
  margin: 8px 0 0;
}

.margin-crud {
  margin: 0 2px;
}

.stock-filters,
.stock-table-card {
  background: rgba(23, 38, 57, 0.55) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}
.stock-filters {
  position: relative;
  z-index: 20;
}

.stock-table-card {
  position: relative;
  z-index: 1;
  border-radius: 6px;
  overflow: hidden;
}

.btn-filters-toggle {
  display: none;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  border: none;
  color: inherit;
  font-weight: 600;
  font-size: 14px;
  padding: 14px 16px;
  cursor: pointer;
}
.btn-filters-toggle .chevron {
  transition: transform .15s;
  color: rgba(255,255,255,.5);
}
.btn-filters-toggle .chevron.open { transform: rotate(180deg); }


/* LIGHT MODE */

body:not(.dark-layout) .stock-filters,
body:not(.dark-layout) .stock-table-card {
  background: rgba(255, 255, 255, 0.55) !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

body:not(.dark-layout) .stock-table thead th {
  background-color: rgba(255, 255, 255, 0.45) !important;
}

body:not(.dark-layout) .stock-table tbody tr,
body:not(.dark-layout) .stock-table tbody tr:nth-of-type(odd),
body:not(.dark-layout) .stock-table tbody tr:nth-of-type(even),
body:not(.dark-layout) .stock-table tbody td {
  background-color: rgba(255, 255, 255, 0.35) !important;
}

body:not(.dark-layout) .stock-table tr.detail-row td {
  background-color: rgba(255, 255, 255, 0.25) !important;
}


/* RESPONSIVO — tabela cabe em qualquer notebook sem scroll horizontal */
.stock-table {
  width: 100%;
  table-layout: fixed;
}

.stock-table th,
.stock-table td {
  white-space: normal;
  overflow-wrap: break-word;
}

.stock-table thead th {
  font-size: 0.80rem;
}

.stock-table th:nth-child(1) { width: 5%; }   /* expand */
.stock-table th:nth-child(2) { width: 35%; }  /* Modelo */
.stock-table th:nth-child(3) { width: 20%; }  /* Fabricante */
.stock-table th:nth-child(4) { width: 15%; }  /* Quantidade */
.stock-table th:nth-child(5) { width: 12.5%; } /* Preço médio */
.stock-table th:nth-child(6) { width: 12.5%; } /* Preço total */

/* RESPONSIVO — PARA TELAS MOBILE */

@media (max-width: 768px) {
  .btn-filters-toggle { display: flex; }
  .stock-filters .card-body { display: none; }
  .stock-filters .card-body.card-body-mobile-open { display: block; padding-top: 0; }

  .stock-table thead { display: none; }
  .stock-table, .stock-table tbody, .stock-table tr, .stock-table td {
    display: block;
    width: 100% !important;
  }
  .stock-table tr.model-row {
    margin-bottom: 0;
    border-radius: 8px 8px 0 0;
    padding: 14px;
    border: 1px solid rgba(255,255,255,.08);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    position: relative;
  }
  .stock-table tr.detail-row {
    margin-bottom: 14px;
    border: 1px solid rgba(255,255,255,.08);
    border-top: none;
    border-radius: 0 0 8px 8px;
  }

  .stock-table td[data-label]::before {
    content: attr(data-label);
    display: block;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: .3px;
    text-transform: uppercase;
    color: rgba(159,168,218,.65);
    margin-bottom: 4px;
  }
  .stock-table td[data-label] {
    text-align: left !important;
    box-sizing: border-box;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
  }

  .stock-table .expand-col {
    order: 0;
    flex: 0 0 auto;
  }

  .stock-table td[data-label="Modelo"] { order: 10; flex: 1 1 100%; font-size: 17px; font-weight: 700; color: #fff; }
  .stock-table td[data-label="Modelo"]::before { content: none; }

  .stock-table td[data-label="Fabricante"],
  .stock-table td[data-label="Quantidade"] { order: 20; flex: 0 1 calc(50% - 6px); }

  .stock-table td[data-label="Preço médio"],
  .stock-table td[data-label="Preço total"] {
    order: 30;
    flex: 0 1 calc(50% - 6px);
    text-align: left !important;
  }

  body:not(.dark-layout) .stock-table td[data-label]::before {
    color: rgba(0,0,0,.45);
  }

  .pagination-bar {
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding-top: 5px;
  }
  .pagination-bar .per-page-selector {
    position: static;
    justify-content: center;
    width: 100%;
  }
  .pagination-bar ::v-deep .VuePagination__count {
    font-size: 12px;
    text-align: center;
  }

  .stock-filters .row.mt-3 > .col-md-12 {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
  }
  .stock-filters .row.mt-3 .btn {
    flex: 1 1 0;
    margin-left: 0 !important;
    white-space: nowrap;
  }

  .stock-header { flex-direction: column; align-items: stretch !important; }
  .stock-header-actions { display: flex; flex-direction: column; gap: 8px; width: 100%; }
  .stock-header-actions .btn { width: 100%; margin-right: 0 !important; text-align: center; }
}
</style>
